// NomOS sessions kept in this browser, one per tenant (Tenant Zero B7: several customers on one build).
//
// A NomOS session is bound to ONE tenant: /auth/login takes a tenant_slug, and GET /me returns the principal's
// single tenant_id. A person who works for several customers therefore holds one session per tenant; the
// tenant switcher moves between the sessions held here. Storage only, no network (client.ts imports this).
//
// localStorage:
//   nomos_sessions  JSON { [slug]: NomosSession }
//   nomos_tenant    slug of the active session
//   nomos_token     the active session's bearer (mirror; the rest of the app and api.ts read it)

export interface NomosSession {
  slug: string;
  token: string;
  tenant_id: string;
  tenant_name?: string | null;
  email?: string | null;
  expires_at?: string | null;
}

const SESSIONS = 'nomos_sessions';
const ACTIVE = 'nomos_tenant';
const TOKEN = 'nomos_token';
const LAST = 'nomos_last_tenant';

function get(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; }
}
function set(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
  } catch { /* storage blocked */ }
}

function expired(s: NomosSession, now = Date.now()): boolean {
  return !!s.expires_at && Date.parse(s.expires_at) <= now;
}

/** Every unexpired session held in this browser, by slug. */
export function listSessions(): NomosSession[] {
  let raw: unknown;
  try { raw = JSON.parse(get(SESSIONS) ?? '{}'); } catch { raw = {}; }
  const all = Object.values((raw && typeof raw === 'object' ? raw : {}) as Record<string, NomosSession>)
    .filter((s) => s && typeof s.slug === 'string' && typeof s.token === 'string');
  return all.filter((s) => !expired(s)).sort((a, b) => (a.tenant_name ?? a.slug).localeCompare(b.tenant_name ?? b.slug));
}

function write(list: NomosSession[]): void {
  set(SESSIONS, list.length ? JSON.stringify(Object.fromEntries(list.map((s) => [s.slug, s]))) : null);
}

/** The active session, if its token is still the one in nomos_token (anything else is stale). */
export function activeSession(): NomosSession | null {
  const slug = get(ACTIVE);
  const token = get(TOKEN);
  if (!slug || !token) return null;
  const s = listSessions().find((x) => x.slug === slug);
  return s && s.token === token ? s : null;
}

export function activeSessionSlug(): string | null {
  return activeSession()?.slug ?? null;
}

/** Store (or replace) the session for its tenant and make it active. */
export function saveSession(s: NomosSession): void {
  write([...listSessions().filter((x) => x.slug !== s.slug), s]);
  activate(s.slug);
}

/** Make a held session active. Returns false when there is no session for `slug`. */
export function activate(slug: string): boolean {
  const s = listSessions().find((x) => x.slug === slug);
  if (!s) return false;
  set(ACTIVE, s.slug);
  set(LAST, s.slug);
  set(TOKEN, s.token);
  return true;
}

/** Leave the active session (kept, so it can be resumed) without signing out: used to sign in to another tenant. */
export function deactivate(): void {
  set(ACTIVE, null);
  set(TOKEN, null);
}

/** Forget the active session (signed out, or rejected with 401). Other tenants' sessions are kept. */
export function dropActiveSession(): void {
  const slug = get(ACTIVE);
  if (slug) write(listSessions().filter((x) => x.slug !== slug));
  deactivate();
}

/** Forget every session in this browser. */
export function dropAllSessions(): void {
  set(SESSIONS, null);
  deactivate();
}

/** Last tenant signed in to here (pre-fills the login form's organisation). */
export function lastTenantSlug(): string | null {
  return get(LAST);
}
